/**
 * GPS/位置追跡系メソッド
 * App.vueから切り出し
 */
import maplibregl from 'maplibre-gl'
import proj4 from 'proj4'
import * as turf from '@turf/turf'
import {
  epsgFromZahyokei, pickNearestVertex, xyFromProps, xyFromLngLat,
  fetchElevation,
  // @ts-ignore
} from '@/js/utils/app'
// @ts-ignore
import { LngLatToAddress, zahyokei } from '@/js/downLoad'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VM = any

// @ts-ignore
declare const Encoding: any

/** ポーリング版 startWatchPosition */
export function startWatchPosition(vm: VM) {
  if (!('geolocation' in navigator)) {
    console.warn('[geo] navigator.geolocation 未対応')
    return
  }
  try { vm.stopWatchPosition() } catch (_) {}
  if (vm.geoPollTimer) { clearInterval(vm.geoPollTimer); vm.geoPollTimer = null }

  const opt = { enableHighAccuracy: true, maximumAge: 0, timeout: 900 }
  let busy = false

  const tick = () => {
    if (busy) return
    busy = true

    navigator.geolocation.getCurrentPosition(
      (position) => {
        vm.geoLastTs = position.timestamp || Date.now()
        try {
          const isUpdateLocation = vm.saveGeoMetrics(position)
          if (isUpdateLocation) {
            const c = position.coords || {}
            const acc = (typeof c.accuracy === 'number') ? c.accuracy : null
            const altAcc = (typeof c.altitudeAccuracy === 'number') ? c.altitudeAccuracy : null
            const quality = vm.getGeoQualityLabel(acc, altAcc)
            const nowTs = position.timestamp || Date.now()

            const allowUpdate =
              quality === 'RTK級' ||
              !vm.lastRtkAt ||
              (nowTs - vm.lastRtkAt) > vm.rtkWindowMs

            if (allowUpdate) {
              if (quality === 'RTK級') vm.lastRtkAt = nowTs

              vm.currentLngLat = [
                Number(position.coords.longitude),
                Number(position.coords.latitude)
              ]

              vm.maybeFocusCenterIncludePoint(
                vm.currentLngLat,
                vm.snapLngLat,
                { padding: vm.isSmall500 ? 80 : 120, animate: true }
              )

              vm.updateLocationAndCoordinates(position)
            } else {
              vm.$store.state.loading3 = true
              vm.$store.state.loadingMessage3 = '座標品質悪化'
              setTimeout(() => { vm.$store.state.loading3 = false }, 1000)
            }
          }
        } catch (e) {
          console.warn('[geo] update/save error', e)
        } finally {
          busy = false
        }
      },
      (error) => {
        console.warn('[geo] error(getCurrentPosition)', error)
        busy = false
      },
      opt
    )
  }

  vm.isTracking = !vm.s_isKuiuchi
  vm.$store.state.isWatching = vm.isTracking
  vm.attachGpsLineClick()
  try { if (vm.logEnabled) vm.maybeLogPoint('start') } catch(_) {}

  vm.geoPollTimer = setInterval(tick, 1000)
  tick()
}

export function stopWatchPosition(vm: VM) {
  if (vm.watchId !== null) {
    try { navigator.geolocation.clearWatch(vm.watchId) } catch(_) {}
    vm.watchId = null
  }
  if (vm.geoPollTimer) {
    clearInterval(vm.geoPollTimer)
    vm.geoPollTimer = null
  }
  vm.detachGpsLineClick()
  vm.clearGpsLine()
  vm.gpsLineAnchorLngLat = null
  vm.$store.state.distance = null
  try { if (vm.logEnabled) vm.maybeLogPoint('stop') } catch(_) {}
}

export function saveGeoMetrics(vm: VM, pos: any) {
  try {
    vm.rtkWindowMs = (vm.rtkWindowMs ?? 1000)
    vm.lastRtkAt = (vm.lastRtkAt ?? 0)

    const c = pos.coords || {}
    const now = pos.timestamp || Date.now()
    const s = vm.$store?.state; if (!s) return

    const prev = s.geo || {}
    const alpha = vm.geoAccAvgAlpha
    const ewma = (prevVal: any, newVal: any) => {
      if (newVal == null || Number.isNaN(newVal)) return prevVal ?? null
      if (prevVal == null) return newVal
      return alpha * newVal + (1 - alpha) * prevVal
    }

    const accuracy = (typeof c.accuracy === 'number') ? c.accuracy : null
    const altitudeAccuracy = (typeof c.altitudeAccuracy === 'number') ? c.altitudeAccuracy : null
    const speed = (typeof c.speed === 'number') ? c.speed : null
    const heading = (typeof c.heading === 'number') ? c.heading : null
    const accuracyAvg = ewma(prev.accuracyAvg, accuracy)
    const quality = getGeoQualityLabel(accuracy, altitudeAccuracy)

    const geo = {
      time: now,
      lat: (typeof c.latitude === 'number') ? c.latitude : prev.lat ?? null,
      lon: (typeof c.longitude === 'number') ? c.longitude : prev.lon ?? null,
      altitude: (typeof c.altitude === 'number') ? c.altitude : prev.altitude ?? null,
      accuracy,
      accuracyAvg,
      altitudeAccuracy,
      speed,
      heading,
      quality,
      source: 'navigator',
    }

    const nowTs = pos.timestamp || Date.now()
    if (quality === 'RTK級') {
      vm.lastRtkAt = nowTs
    }
    if (quality !== 'RTK級' && vm.lastRtkAt && (nowTs - vm.lastRtkAt) <= vm.rtkWindowMs) {
      return false
    }

    s.geo = geo
    vm.prevQuality = quality

    try { vm.maybeLogPoint('point') } catch(_) {}
    try { if (vm.gpsLineAnchorLngLat) vm.drawGpsLine(vm.gpsLineAnchorLngLat) } catch (_) {}

    return true
  } catch (e) {
    console.warn('[geo] saveGeoMetrics error', e)
  }
}

export function getGeoQualityLabel(_acc: number | null, _altAcc: number | null) {
  const acc = _acc
  if (acc == null) return 'unknown'
  if (acc <= 1) return 'RTK級'
  if (acc <= 3) return '高'
  if (acc <= 10) return '中'
  if (acc <= 30) return '低'
  return '低'
}

export function drawGpsLine(vm: VM, clickLngLat: any) {
  const map = vm.$store?.state?.map01
  if (!map) return

  const s = vm.$store?.state || {}
  const geo = s.geo
  if (!geo || geo.lat == null || geo.lon == null) return

  const epsg = epsgFromZahyokei(s.s_zahyokei || s.zahyokei, zahyokei)
  if (!epsg) return

  let p1: any
  let anchorProps: any = null
  if (clickLngLat && clickLngLat.__anchor) {
    p1 = { lng: clickLngLat.lng, lat: clickLngLat.lat }
    anchorProps = clickLngLat.properties || null
  } else {
    const snapped = pickNearestVertex(map, clickLngLat, 16, vm.snapLayerIds)
    if (!snapped) return
    p1 = { lng: snapped.lng, lat: snapped.lat }
    anchorProps = snapped.properties || null
    vm.gpsLineAnchorLngLat = { ...p1, __anchor: true, properties: anchorProps }
    try { if (vm.logEnabled) vm.maybeLogPoint('anchor') } catch(_) {}
  }

  const p2 = { lng: geo.lon, lat: geo.lat }

  const fromProps = xyFromProps(anchorProps)
  let p1xy: any
  if (fromProps) {
    p1xy = { x: fromProps.x, y: fromProps.y }
  } else {
    p1xy = xyFromLngLat(p1.lng, p1.lat, epsg)
    if (!p1xy) return
  }

  const jdp = vm.$store?.state?.jdpCoordinates
  if (!Array.isArray(jdp) || jdp.length < 2) return
  const p2xy = { x: Number(jdp[1]), y: Number(jdp[0]) }
  if (!Number.isFinite(p2xy.x) || !Number.isFinite(p2xy.y)) return

  const dx = p2xy.x - p1xy.x
  const dy = p2xy.y - p1xy.y
  const meters = Math.hypot(dx, dy)

  const mid = turf.midpoint([p1.lng, p1.lat], [p2.lng, p2.lat])

  let text: string
  if (meters < 1) text = `約${(meters * 100).toFixed(0)}cm`
  else if (meters < 1000) text = `約${meters.toFixed(1)}m`
  else text = `約${(meters / 1000).toFixed(1)}km`

  vm.$store.state.distance = `距離= ${text}`

  const toRad = (d: number) => d * Math.PI / 180
  const lon1 = toRad(p1.lng), lat1 = toRad(p1.lat)
  const lon2 = toRad(p2.lng), lat2 = toRad(p2.lat)
  const dLon = lon2 - lon1
  const yb = Math.sin(dLon) * Math.cos(lat2)
  const xb = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)
  let bearing = (Math.atan2(yb, xb) * 180 / Math.PI + 360) % 360
  bearing = (bearing + 90) % 360
  if (bearing > 90 && bearing <= 270) bearing = (bearing + 180) % 360

  const LINE_SRC = 'oh-gps-line-src'
  const LINE_LAYER = 'oh-gps-line'
  const LAB_SRC = 'oh-gps-line-label-src'
  const LAB_LAYER = 'oh-gps-line-label'

  const line = turf.lineString([[p1.lng, p1.lat], [p2.lng, p2.lat]])
  if (map.getSource(LINE_SRC)) map.getSource(LINE_SRC).setData(line)
  else map.addSource(LINE_SRC, { type: 'geojson', data: line })

  if (!map.getLayer(LINE_LAYER)) {
    map.addLayer({
      id: LINE_LAYER,
      type: 'line',
      source: LINE_SRC,
      paint: { 'line-color': '#00B8D9', 'line-width': 3 }
    })
  }

  const labelFC = turf.featureCollection([
    turf.point(mid.geometry.coordinates, { label: text, angle: bearing })
  ])

  if (map.getSource(LAB_SRC)) map.getSource(LAB_SRC).setData(labelFC)
  else map.addSource(LAB_SRC, { type: 'geojson', data: labelFC })

  if (!map.getLayer(LAB_LAYER)) {
    map.addLayer({
      id: LAB_LAYER,
      type: 'symbol',
      source: LAB_SRC,
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 14,
        'text-allow-overlap': true,
        'text-rotation-alignment': 'map',
        'text-rotate': ['get', 'angle']
      },
      paint: { 'text-halo-color': 'white', 'text-halo-width': 1.5 }
    })
  }

  vm.snapLngLat = [p1.lng, p1.lat]
  vm.focusCenterIncludePoint(vm.currentLngLat, vm.snapLngLat, { padding: vm.isSmall500 ? 80 : 120, animate: true })
}

export function attachGpsLineClick(vm: VM) {
  const map = vm.$store?.state?.map01
  if (!map) return

  if (vm.onMapClickForGpsLine) {
    vm.enableGpsLineClick = true
    return
  }

  const bind = () => {
    vm.enableGpsLineClick = true
    vm.onMapClickForGpsLine = (e: any) => {
      if (!vm.enableGpsLineClick) return
      if (!e || !e.lngLat) return
      vm.drawGpsLine(e.lngLat)
    }
    map.on('click', vm.onMapClickForGpsLine)
  }

  try {
    if (map.isStyleLoaded && map.isStyleLoaded()) bind()
    else map.once('load', bind)
  } catch (_) {
    map.once('load', bind)
  }
}

export function detachGpsLineClick(vm: VM) {
  const map = vm.$store?.state?.map01
  if (!map) return
  if (vm.onMapClickForGpsLine) {
    try { map.off('click', vm.onMapClickForGpsLine) } catch(_) {}
    vm.onMapClickForGpsLine = null
  }
  vm.enableGpsLineClick = false
}

export function clearGpsLine(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return
  const LINE_SRC = 'oh-gps-line-src'
  const LINE_LAYER = 'oh-gps-line'
  const LAB_SRC = 'oh-gps-line-label-src'
  const LAB_LAYER = 'oh-gps-line-label'
  try { if (map.getLayer(LAB_LAYER)) map.removeLayer(LAB_LAYER) } catch(_) {}
  try { if (map.getSource(LAB_SRC)) map.removeSource(LAB_SRC) } catch(_) {}
  try { if (map.getLayer(LINE_LAYER)) map.removeLayer(LINE_LAYER) } catch(_) {}
  try { if (map.getSource(LINE_SRC)) map.removeSource(LINE_SRC) } catch(_) {}
}

export async function watchPositionHandler(vm: VM, up: string) {
  vm.$store.state.commonDialog.watchPositionOpen = false
  if (up === 'close') {
    vm.toggleWatchPosition(null, true)
    return
  }
  if (up === 'h') {
    vm.isHeadingUp = true; try { vm.compass?.turnOn?.() } catch(_) {}
  } else {
    vm.isHeadingUp = false; try { vm.compass?.turnOff?.() } catch(_) {}
  }
  try { vm.currentMarker?.remove?.() } catch(_) {}
  vm.startWatchPosition()
}

export async function toggleWatchPosition(vm: VM, mode: any, isClose: boolean) {
  vm.isTracking = false
  vm.$store.state.isWatching = false
  vm.s_isKuiuchi = false
  vm.isHeadingUp = false
  vm.stopWatchPosition()
  try { vm.centerMarker?.remove?.() } catch(_) {}
  vm.centerMarker = null
  try { vm.currentMarker?.remove?.() } catch(_) {}
  vm.currentMarker = null
  try { vm.compass?.turnOff?.() } catch(_) {}
  const map = vm.$store?.state?.map01; try { map?.resetNorthPitch?.() } catch(_) {}
  vm.$store.state.geo = null
  vm.detachGpsLineClick()
  vm.clearGpsLine()
  vm.gpsLineAnchorLngLat = null
  if (!isClose) {
    vm.$store.state.commonDialog.watchPositionOpen = true
    if (mode === 'k') {
      vm.isTracking = false
      vm.$store.state.isWatching = false
      vm.s_isKuiuchi = true
    } else {
      vm.isTracking = true
      vm.$store.state.isWatching = true
      vm.s_isKuiuchi = false
    }
  }
}

export function requestClearLog(vm: VM) { vm.confirmClearLog = true }

export function doClearLog(vm: VM) {
  if (vm.logEnabled) vm.stopTrackLog()
  vm.clearTrackLog()
  vm.confirmClearLog = false
}

export function toggleTrackLog(vm: VM) {
  if (vm.logEnabled) {
    vm.stopTrackLog()
  } else {
    vm.startTrackLog()
  }
}

export function startTrackLog(vm: VM) {
  if (!vm.csvRows) {
    vm.csvRows = [[
      'timestamp', 'lat', 'lon', 'X', 'Y', 'CRS',
      'accuracy', 'quality', 'eventType'
    ]]
  }
  vm.logEnabled = true
  vm.lastLogAt = 0
  vm.lastLogXY = null
  try { vm.maybeLogPoint('start') } catch(_) {}
}

export function stopTrackLog(vm: VM) {
  vm.logEnabled = false
  try { vm.maybeLogPoint('stop') } catch(_) {}
}

export function clearTrackLog(vm: VM) {
  vm.csvRows = null
  vm.lastLogAt = 0
  vm.lastLogXY = null
}

export function maybeLogPoint(vm: VM, eventType = 'point') {
  if (!vm.logEnabled) return

  const s = vm.$store?.state || {}
  const geo = s.geo; if (!geo) return

  const csLabel = s.s_zahyokei || s.zahyokei || ''

  const jdp = s.jdpCoordinates
  if (!Array.isArray(jdp) || jdp.length < 2) return
  const xN = Number(jdp[1])
  const yE = Number(jdp[0])
  if (!Number.isFinite(xN) || !Number.isFinite(yE)) return

  const now = Date.now()

  if (vm.lastLogAt && (now - vm.lastLogAt) < vm.minLogIntervalMs) return

  if (vm.lastLogXY) {
    const dx = xN - vm.lastLogXY.x
    const dy = yE - vm.lastLogXY.y
    const dd = Math.hypot(dx, dy)
    if (dd < vm.minLogDistanceM) return
  }

  if (!vm.csvRows) {
    vm.csvRows = [[
      'timestamp', 'lat', 'lon', 'X', 'Y', 'CRS',
      'accuracy', 'quality', 'eventType'
    ]]
  }
  vm.csvRows.push([
    $_jstLocal(),
    geo.lat, geo.lon,
    xN, yE, csLabel,
    geo.accuracy,
    geo.quality,
    eventType
  ])
  vm.lastLogAt = now
  vm.lastLogXY = { x: xN, y: yE }
}

export function exportTrackCsv(vm: VM) {
  if (!vm.csvRows || vm.csvRows.length <= 1) return

  const csvEscape = (v: any) => {
    if (v == null) return ''
    const s = (typeof v === 'object') ? JSON.stringify(v) : String(v)
    return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }

  const csv = vm.csvRows
    .map((row: any[]) => row.map(csvEscape).join(','))
    .join('\r\n') + '\r\n'

  $_downloadText(
    csv,
    `track_${$_jstStamp()}.csv`,
    'text/csv;charset=utf-8;'
  )
}

export function exportTrackSima(vm: VM, title: string) {
  if (!vm.csvRows || vm.csvRows.length <= 1) return

  const header = vm.csvRows[0]
  const col = (name: string) => header.indexOf(name)
  const idx_ts = col('timestamp')
  const idx_x = col('X')
  const idx_y = col('Y')
  const idx_cs = col('CRS')

  const rows = vm.csvRows.slice(1)
  if (!rows.length) return

  const siteName = title || (vm.$store?.state?.siteName ?? 'OH3出力')
  const csLabel = (rows[0] && rows[0][idx_cs]) || (vm.$store?.state?.s_zahyokei || vm.$store?.state?.zahyokei || '')

  const head = [
    `G00,01,${siteName} 座標`,
    `Z00,座標データ,`,
    `G01,座標系,${csLabel}`,
    `A00,`
  ]
  const pad = (n: number, w = 2) => String(n).padStart(w, '0')

  let n = 0
  const a01 = rows.map((r: any[]) => {
    const ts = r[idx_ts]
    const Xn = Number(r[idx_x])
    const Ye = Number(r[idx_y])
    if (!Number.isFinite(Xn) || !Number.isFinite(Ye)) return null

    const d = new Date(ts)
    const ptName = `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`

    n += 1
    return `A01,${n},${ptName},${Xn.toFixed(3)},${Ye.toFixed(3)},,`
  }).filter(Boolean)

  if (!a01.length) return

  const simTxt = [...head, ...a01].join('\r\n') + '\r\n'

  const sjisBytes = $_toShiftJisBytes(simTxt)
  const blob = new Blob([sjisBytes], { type: 'application/octet-stream' })
  $_downloadBlob(blob, `track_${$_jstStamp()}.sim`)
}

// 汎用ユーティリティ（日時/保存/クランプ/終了処理）
export function $_jstLocal() {
  const p: any = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  }).formatToParts(new Date()).reduce((a: any, x: any) => (a[x.type] = x.value, a), {})
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`
}

export function $_jstStamp() {
  const parts: any = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  }).formatToParts(new Date()).reduce((acc: any, p: any) => (acc[p.type] = p.value, acc), {})
  return `${parts.year}-${parts.month}-${parts.day}_${parts.hour}-${parts.minute}-${parts.second}`
}

export function $_toShiftJisBytes(text: string) {
  try {
    if (typeof Encoding !== 'undefined') {
      const unicodeArr = Encoding.stringToCode(text)
      const sjisBytes = Encoding.convert(unicodeArr, 'SJIS', 'UNICODE')
      return new Uint8Array(sjisBytes)
    }
  } catch (e) {
    console.warn('[export] SJIS convert failed, fallback to UTF-8', e)
  }
  return new TextEncoder().encode(text)
}

export function $_downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function $_downloadText(text: string, filename: string, mime: string) {
  const blob = new Blob([text], { type: mime })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}

export function updateLocationAndCoordinates(vm: VM, position: any) {
  const map = vm.$store.state.map01
  let longitude: number, latitude: number
  if (position) {
    ({ latitude, longitude } = position.coords)
    map.flyTo({ center: [longitude, latitude], zoom: map.getZoom(), animate: true })
    map.once('moveend', () => {
      const center = map.getCenter()
      const centerPoint = map.project([center.lng, center.lat])
      const features = map.queryRenderedFeatures(centerPoint, {
        layers: ['zones-layer']
      })
      if (features.length > 0) {
        const zoneFeature = features[0]
        const zone = zoneFeature.properties.zone
        vm.$store.state.zahyokei = '公共座標' + zone + '系'
      } else {
        vm.$store.state.zahyokei = ''
      }
      vm.zoom = map.getZoom()
    })
  } else {
    const center = map.getCenter()
    longitude = center.lng
    latitude = center.lat
  }
  if (!vm.centerMarker) {
    vm.centerMarker = new maplibregl.Marker({ color: 'green' })
      .setLngLat([longitude, latitude])
      .addTo(map)
    vm.currentLngLat = [longitude, latitude]
  } else {
    vm.centerMarker.setLngLat([longitude, latitude])
  }
}

export function goToCurrentLocation(vm: VM) {
  if ('geolocation' in navigator) {
    const opt = { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLongitude = position.coords.longitude
        const userLatitude = position.coords.latitude

        const map = vm.$store.state.map01
        if (!map) return
        map.scrollZoom.disable()
        map.dragPan.disable()
        map.keyboard.disable()
        map.doubleClickZoom.disable()
        map.flyTo({
          center: [userLongitude, userLatitude],
          zoom: 15.01,
          essential: true
        })
        map.once('moveend', () => {
          map.scrollZoom.enable()
          map.dragPan.enable()
          map.keyboard.enable()
          map.doubleClickZoom.enable()
        })
        vm.$store.commit('setCurrentLocation', {
          lng: userLongitude,
          lat: userLatitude
        })

        const MIN_DISTANCE = 0.0001
        if (vm.lastCurrentLocationLngLat) {
          const dLng = Math.abs(userLongitude - vm.lastCurrentLocationLngLat[0])
          const dLat = Math.abs(userLatitude - vm.lastCurrentLocationLngLat[1])
          if (dLng < MIN_DISTANCE && dLat < MIN_DISTANCE) {
            return
          }
        }
        vm.lastCurrentLocationLngLat = [userLongitude, userLatitude]

        LngLatToAddress(userLongitude, userLatitude).then(async (address: string) => {
          const title = address || '現在位置'

          const savedMarker = await vm.$store.dispatch('addSavedMarker', {
            lng: userLongitude,
            lat: userLatitude,
            title: title,
            marker_type: 'current_location'
          })
          const markerId = savedMarker?.id || null

          const popupContent = vm.createCurrentLocationPopupContent(userLongitude, userLatitude, title, markerId)
          const popup = new maplibregl.Popup({
            offset: 25,
            closeButton: true,
            closeOnClick: false,
            maxWidth: '280px'
          }).setHTML(popupContent)

          const marker = new maplibregl.Marker({ color: '#1976D2' })
            .setLngLat([userLongitude, userLatitude])
            .setPopup(popup)
            .addTo(map)

          popup.on('open', () => {
            vm.setupCurrentLocationPopupButtons(popup, marker, userLongitude, userLatitude, markerId)
          })

          marker.getElement().style.cursor = 'pointer'
        }).catch(async () => {
          const title = '現在位置'

          const savedMarker = await vm.$store.dispatch('addSavedMarker', {
            lng: userLongitude,
            lat: userLatitude,
            title: title,
            marker_type: 'current_location'
          })
          const markerId = savedMarker?.id || null

          const popupContent = vm.createCurrentLocationPopupContent(userLongitude, userLatitude, title, markerId)
          const popup = new maplibregl.Popup({
            offset: 25,
            closeButton: true,
            closeOnClick: false,
            maxWidth: '280px'
          }).setHTML(popupContent)

          const marker = new maplibregl.Marker({ color: '#1976D2' })
            .setLngLat([userLongitude, userLatitude])
            .setPopup(popup)
            .addTo(map)

          popup.on('open', () => {
            vm.setupCurrentLocationPopupButtons(popup, marker, userLongitude, userLatitude, markerId)
          })

          marker.getElement().style.cursor = 'pointer'
        })
      },
      (error) => {
        console.error("現在位置の取得に失敗しました:", error)
        let errorMsg = '現在位置を取得できませんでした'
        if (error.code === 1) errorMsg = '位置情報の使用が許可されていません'
        else if (error.code === 3) errorMsg = '位置情報の取得がタイムアウトしました'
        vm.snackbarText = errorMsg
        vm.snackbar = true
      },
      opt
    )
  } else {
    console.error("Geolocationはこのブラウザでサポートされていません。")
  }
}

export function createCurrentLocationPopupContent(_vm: VM, lng: number, lat: number, title = '現在位置', markerId: string | null = null) {
  return `
    <div class="marker-popup" data-marker-id="${markerId || ''}">
      <div class="popup-title">${title}</div>
      <div class="popup-actions">
        <button class="popup-btn popup-btn-copy" data-lng="${lng}" data-lat="${lat}" title="地点情報をコピー">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
          </svg>
          コピー
        </button>
        <button class="popup-btn popup-btn-delete" title="マーカーを削除">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
          削除
        </button>
      </div>
    </div>
  `
}

export function setupCurrentLocationPopupButtons(vm: VM, popup: any, marker: any, lng: number, lat: number, markerId: string | null = null) {
  const popupEl = popup.getElement()
  if (!popupEl) return

  const copyBtn = popupEl.querySelector('.popup-btn-copy')
  if (copyBtn) {
    copyBtn.addEventListener('click', async (e: Event) => {
      e.stopPropagation()
      await vm.copyCurrentLocationInfo(lng, lat, copyBtn)
    })
  }

  const deleteBtn = popupEl.querySelector('.popup-btn-delete')
  if (deleteBtn) {
    deleteBtn.addEventListener('click', (e: Event) => {
      e.stopPropagation()
      marker.remove()
      vm.lastCurrentLocationLngLat = null
      if (markerId) {
        vm.$store.dispatch('removeSavedMarker', markerId)
      }
    })
  }
}

export async function copyCurrentLocationInfo(vm: VM, lng: number, lat: number, btn: HTMLButtonElement) {
  const originalText = btn.innerHTML
  btn.innerHTML = `
    <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="31.4" stroke-dashoffset="10">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
      </circle>
    </svg>
  `
  btn.disabled = true

  try {
    const [elevation, address] = await Promise.all([
      fetchElevation(lng, lat).catch(() => null),
      LngLatToAddress(lng, lat).catch(() => null)
    ])

    const zahyokeiLabel = vm.$store.state.zahyokei || ''
    const zahyokeiItem = zahyokei.find((item: any) => item.kei === zahyokeiLabel)

    let jdpCoords = ''
    if (zahyokeiItem) {
      try {
        const jdp = proj4('EPSG:4326', zahyokeiItem.code, [lng, lat])
        jdpCoords = `${jdp[1].toFixed(3)}, ${jdp[0].toFixed(3)}`
      } catch (e) {
        console.warn('JDP座標変換エラー:', e)
      }
    }

    const wgs84Coords = `${lng.toFixed(6)}, ${lat.toFixed(6)}`

    const items: string[] = []
    if (elevation !== null && elevation !== undefined && elevation <= 4000) {
      items.push(`標高: 中心の標高: ${elevation.toFixed(2)}m`)
    }
    if (address) {
      items.push(`住所: ${address}`)
    }
    if (zahyokeiLabel) {
      items.push(`座標系: ${zahyokeiLabel}`)
    }
    if (jdpCoords) {
      items.push(`JDP座標: ${jdpCoords}`)
    }
    items.push(`WGS84座標: ${wgs84Coords}`)

    const copyText = items.join('\n')
    await navigator.clipboard.writeText(copyText)

    btn.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
      </svg>
      完了
    `
    btn.style.backgroundColor = '#4caf50'
    btn.style.color = 'white'

    setTimeout(() => {
      btn.innerHTML = originalText
      btn.style.backgroundColor = ''
      btn.style.color = ''
      btn.disabled = false
    }, 1500)

  } catch (err) {
    console.error('地点情報の取得またはコピーに失敗:', err)
    const fallbackText = `WGS84座標: ${lng.toFixed(6)}, ${lat.toFixed(6)}`
    try {
      await navigator.clipboard.writeText(fallbackText)
    } catch (e) {
      const textArea = document.createElement('textarea')
      textArea.value = fallbackText
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
    }
    btn.innerHTML = originalText
    btn.disabled = false
  }
}
